<template>
  <div>
    <b-card>
      <h3 class="mb-2">
        Privacy Policy
      </h3>

      <h5>Scope</h5>

      <p>
        The {{ brandName }} Service is available to UK financial professionals and other
        invited parties under relevant subscriber agreements. We collect
        website usage data and may provide this data in aggregate to product providers. This means that a product provider will be able to see how many users are
        accessing web pages relating to their products. Under no circumstances will personal or
        individually identifiable information be made available to them.
      </p>

      <p>
        All personal data is collected and stored in line with the General Data Protection Regulation (GDPR). Any
        personal information that you provide to us will only be used for the purpose stated at the time we
        request it. Your personal data that you have provided will not be sent to third parties and will
        remain confidential.
      </p>

      <p>
        This policy and therefore the ways in which your data may be processed can be changed from time to time. Any
        changes will be notified to you and will appear on this web page.
      </p>

      <p>
        If you have any queries relating to this policy please <router-link :to="{name: 'contact-us'}">
          contact us
        </router-link>.
      </p>

      <h5 class="my-2">
        Introduction
      </h5>

      <p>
        This is the privacy policy of Future Value Consultants Ltd <span class="font-weight-bold">(hereafter FVC)</span>
        , a company registered in England with registered number 3576707, and whose registered office is at Menzies LLP, Magna House, 18-32 London Road, Staines-Upon-Thames, United Kingdom, TW18 4BP.
      </p>

      <p>
        FVC respects your privacy and is committed to ensuring that your personal information is used in accordance with
        current data protection legislation to be introduced as the General Data Protection Regulation (GDPR)
        effective 25 May 2018.
      </p>

      <p>
        This policy is structured in the following manner to aid your understanding of:
      </p>

      <ul>
        <li>What information do we hold on you?</li>
        <li>How is your data collected?</li>
        <li>How do we use your information?</li>
        <li>Who do we share your information with?</li>
        <li>How long do we retain your information?</li>
        <li>What are your rights in relation to your information?</li>
        <li>Contact point in relation to this policy</li>
      </ul>

      <p>
        For the purposes of Personal Data processing FVC identifies the following Data Subject categories:
      </p>

      <ul>
        <li>
          Employee or representative of an Investment Bank, Plan Manager or Distributor of Structured Products
          <span class="font-weight-bold">(Institutional User)</span>
        </li>
        <li>
          Financial Adviser Subscriber to the Structured Edge Service, (either Standard or Premium)
          <span class="font-weight-bold">({{ brandName }} Subscriber)</span>- you have requested to access one of our free, or paid, online services.
          <br>
          It should be noted that no private investor or individual users are allowed on this service therefore this
          privacy policy takes no account of such users.
        </li>
      </ul>

      <p>
        We have therefore split this privacy notice into those two categories for clarity and readability.
      </p>

      <p>
        You may belong to more than one category.
      </p>

      <h5 class="my-2">
        <u>Institutional User</u>
      </h5>

      <h5 class="my-2">
        What information do we hold on you?
      </h5>

      <p>
        We collect your name, role, firm name, email address, telephone number and business address.
      </p>

      <p>
        When you connect to our services, we log your IP address.
      </p>

      <h5 class="my-2">
        How is your data collected?
      </h5>

      <p>
        If your firm has expressed an interest in having its structured products appear on {{ brandName }} or becomes a
        supporting partner of {{ brandName }} our team will collect your name, firm name, business address
        and other contact details.
      </p>

      <h5 class="my-2">
        How do we use your information?
      </h5>

      <p class="font-italic">
        Processing for the purpose of providing services:
      </p>

      <p>
        We process your data, relying on the "legitimate interest ground". FVC's legitimate business interest, and
        purpose of our processing of your data, is researching the product terms and market development in the
        structured products markets that it serves. This includes the provision of company and business
        professional contact information such as name, role, business address, phone number and email
        address. Such company information may be displayed in our services. In order to
        provide our contracted services we will process your personal data to enable the
        creation of credentials to access our online services. Logs of access recorded
        by IP address are used in the process of potential system abuse and crime
        detection.
      </p>

      <h5 class="my-2">
        Who do we share your information with?
      </h5>

      <p>
        We process your data using cloud hosting providers in the EU. We will share your company contact details with
        {{ brandName }} Subscribers. Our {{ brandName }} Subscribers include Financial Advisers and Fund Managers in the UK or
        other countries inside or outside the EU.
      </p>

      <h5 class="my-2">
        <u>{{ brandName }} Subscriber</u>
      </h5>

      <h5 class="my-2">
        What information do we hold on you?
      </h5>

      <p>
        We collect your name, role, firm name, company type, FCA number, email address, telephone number, business address and main
        country of business.
      </p>

      <p>
        When you connect to our services we log your IP address and the pages you have viewed.
      </p>

      <p>
        When using the service user-generated content such as saved selections and reports may be created and stored.
      </p>

      <h5 class="my-2">
        How is your data collected?
      </h5>

      <p>
        When you register for the {{ brandName }} service it is with your express interest in researching the structured
        product market to aid your client recommendations and allocations and to evidence your compliance process and
        satisfying of your regulatory requirements.
      </p>

      <p>
        If you have used our services, our systems will collect your IP address for services you have used and pages you
        have visited. We may also collect details on the device you used to connect, such as operating system,
        geographic location, browser type and language, access times and referring website addresses.
      </p>

      <p>
        When using the service user-generated content is created and stored when you interact with specific features of
        the service.
      </p>

      <h5 class="my-2">
        How do we use your information?
      </h5>

      <p class="font-italic">
        Processing for the purpose of providing services:
      </p>
      <p>
        We process your data, relying on the "legitimate interest ground". FVCs legitimate business interest, and
        purpose of our processing of your data, is to provide you with the information that you registered for and
        for you to conduct any detailed searches or product selection process.
      </p>

      <p>
        Report notification emails are an integral part of the Structured Edge service. You will initially be opted in but your preferences can be amended later. We will never send you any spam and
        always aim to ensure that our emails will be genuinely useful to recipients.
      </p>

      <p>
        Our processing of your personal data is in our legitimate interests and is necessary to provide the Structured Edge service
        to you and to continually improve it and the content available through it.
      </p>

      <p>
        Our use of your personal data is limited to that which is reasonably required in order to provide our service to
        you and to make improvements to that service which benefit both us and you. We do not use your personal data for
        any reasons not stated in this Privacy Policy and never use it in an excessive manner that is disproportionate
        to our aims of providing and improving our service or in a way that is contrary to your interests.
      </p>

      <p>
        We wish to monitor on an anonymous aggregate basis usage volumes and
        behaviour of our users. We have a contractual agreement with our Institutional users to provide access
        by Financial Advisers of the structured products they contribute to the service in the form of information
        on our {{ brandName }} websites and through service email communications.
      </p>

      <p>
        This includes the provision of company and business professional contact information such as name, role,
        business address, phone number and email address.
      </p>

      <p>
        In order to provide our contracted services we will process your personal data to enable the creation of
        credentials to access our product.
      </p>

      <p>
        Logs of access recorded by IP address are used in the process of potential system abuse and crime detection.
      </p>

      <p>
        Logs of page views and interaction with our web product are maintained to help us to provide you with a more
        personalized user experience in relation to the provision of services to you.
      </p>

      <p>
        User-generated content such as saved selections and reports are created and stored so that you can use specific
        features of the service and access such content at a later time.
      </p>

      <p class="font-italic">
        Processing for the purpose of analyzing user data:
      </p>
      <p>
        When you receive newsletters or promotional email from FVC, we may use personalized unique links or similar
        technologies to determine whether the email has been opened and which links you click. This enables us to
        provide you with email communications or other information that are more targeted to your needs. We will
        rely on our legitimate interests to do this, meaning the provision of electronic direct marketing of our
        data, services and products, unless legislation requires us to obtain your consent, in which case we
        will request this from you.
      </p>

      <h5 class="my-2">
        Who do we share your information with?
      </h5>

      <p>
        We process your data using cloud hosting providers in the EU.
      </p>

      <p>
        We will share your information with partners that provide analytics, user experience and interaction analysis of
        our web product. This data is pseudonymized, using cookies.
      </p>

      <h5 class="my-2">
        What are your rights in relation to our use of your information?
      </h5>

      <p>
        You have rights under data protection law in relation to our use of your information, including to:
      </p>

      <ol type="a">
        <li>
          request access to your information, which you can do by visiting our contact page stating you are submitting a
          Subject Access Request
        </li>
        <li>
          update or amend your information if it is inaccurate or incomplete by either updating your details on the
          "account details" page or visiting our contact page requesting an update
        </li>
        <li>
          object to certain uses of your personal data (which includes direct marketing and processing based on
          legitimate interests and processing for purposes of scientific or historical research and statistics)
          on grounds relating to your particular situation by visiting our contact page. In order to comply
          with this request we may give you notice that we will elect to permanently delete your account
          to allow us to provide free services on a manageable basis.
        </li>
        <li>
          request the deletion of your information, or restrict its use, in certain circumstances (for example you can
          request that we erase your information where the information is no longer necessary for the purpose for
          which it was collected, unless certain exceptions apply) by visiting our contact page;
        </li>
        <li>
          withdraw any consents you have provided in respect of our use of your information either by clicking account
          delete links in emails, on our website or visiting our contact page;
        </li>
        <li>
          lodge a complaint with the Information Commissioner's Office (https://www.ico.org.uk/global/contact-us/email/)
          [or other relevant supervisory authority].
        </li>
      </ol>

      <p>
        We may need to ask you for further information and identification to help us to comply with this request. More
        information about these rights can be found at https://ico.org.uk/for-the-public/. If you have any questions
        about these rights, or you would like to exercise any of them, please
        <router-link :to="{name: 'contact-us'}">
          contact us
        </router-link>.
      </p>

      <h5 class="my-2">
        How long do we retain your data?
      </h5>

      <p>
        Contact details of Institutional Users and {{ brandName }}  Subscribers are stored for varying lengths depending on the
        nature and purpose for which it was collected. FVC will review your personal data periodically (usually
        annually) to ensure it is still necessary to be retained for the purpose for which it was collected.
      </p>

      <p>
        Page View data meaning statistics as to which firm has accessed your firm profile on our web product and how
        often is kept indefinitely so that we can understand changes to usage patterns over time.
      </p>

      <p>
        System access and event logs, and backups of our products are retained only as long as is necessary to ensure
        reliable, safe and secure operation of our services and to abide by applicable laws and regulations. We have
        processes to ensure if a backup restoration is required Subject Rights are maintained.
      </p>

      <h5 class="my-2">
        Do we use cookies?
      </h5>

      <p>
        Yes (cookies are small files that a site or its service provider transfers to your computers hard drive through
        your web browser (if you allow) that enables the sites or service providers systems to recognize your browser
        and capture and remember certain information.
      </p>

      <p>
        We use cookies to help us remember and process your preferences for future visits, keep track of advertisements
        and compile aggregate data about site traffic and site interaction so that we can offer better site experiences
        and tools in the future. We may contract with third-party service providers to assist us in better
        understanding our site visitors. These service providers are not permitted to use the information
        collected on our behalf except to help us conduct and improve our business.
      </p>

      <p>
        If you prefer, you can choose to have your computer warn you each time a cookie is being sent, or you can choose
        to turn off all cookies via your browser settings. Like most websites, if you turn your cookies off, some of our
        services may not function properly. However, you can still place orders over the telephone or by contacting
        customer service.
      </p>

      <h5 class="my-2">
        How to contact us
      </h5>

      <p>
        If you have any questions regarding this policy, please
        <router-link :to="{name: 'contact-us'}">
          contact us
        </router-link>.
      </p>

      <h5 class="my-2">
        Changes to this policy
      </h5>

      <p>
        We keep this privacy notice under regular review and we will place any updates on this webpage. We will inform
        you of any material changes to the privacy notice. This privacy notice was last updated on 01st September 2026.
      </p>       </b-card>
  </div>
</template>
<script>
import {
  BCard,
} from 'bootstrap-vue'
import { $themeConfig } from '@themeConfig'

export default {
  components: {
    BCard,
  },
  computed: {
    brandName() {
      return this.brand === 'se' ? 'Structured Edge' : 'Future Value Consultants'
    },
    brand() {
      return $themeConfig.app.brand
    },
  },
}
</script>
